import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const usePagination = (items, size = 6, filterKey = '') => {
  const [params, setParams] = useSearchParams();
  const previous = useRef(filterKey);
  const totalPages = Math.max(1, Math.ceil(items.length / size));
  const raw = Number(params.get('page'));
  const page = Number.isSafeInteger(raw) && raw > 0 ? Math.min(raw, totalPages) : 1;
  useEffect(() => {
    if (previous.current !== filterKey) {
      previous.current = filterKey;
      if (params.has('page')) { const next = new URLSearchParams(params); next.delete('page'); setParams(next, { replace: true }); }
    }
  }, [filterKey, params, setParams]);
  const onPage = (next) => {
    const result = new URLSearchParams(params);
    if (next <= 1) result.delete('page'); else result.set('page', String(Math.min(next, totalPages)));
    setParams(result);
  };
  return { items: items.slice((page - 1) * size, page * size), page, totalPages, total: items.length, size, onPage };
};

export const Pagination = ({ page, totalPages, total, size, onPage, testId }) => {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i);
  const style = 'min-w-10 min-h-11 px-3 rounded-lg border border-[#343B48] text-sm text-slate-300 hover:border-amber-500/60 hover:text-amber-400 transition-colors disabled:opacity-30 disabled:pointer-events-none sc-focus';
  return <nav data-testid={`${testId}-pagination`} aria-label={`Páginas de ${testId}`} className="flex flex-col sm:flex-row items-center justify-between gap-4 py-8">
    <p data-testid={`${testId}-page-count`} className="text-xs text-slate-400 font-mono">{total ? `${(page - 1) * size + 1}–${Math.min(page * size, total)} de ${total}` : '0 publicaciones'} · Página {page} de {totalPages}</p>
    <div className="flex gap-1.5 flex-wrap justify-center">
      <button data-testid={`${testId}-page-prev`} aria-label="Página anterior" disabled={page === 1} onClick={() => onPage(page - 1)} className={style}><ChevronLeft size={16} /></button>
      {pages.map((n) => <button key={n} data-testid={`${testId}-page-${n}`} aria-label={`Página ${n}`} aria-current={page === n ? 'page' : undefined} onClick={() => onPage(n)} className={`${style} ${page === n ? 'bg-amber-500 !border-amber-500 !text-slate-950 font-semibold' : 'bg-[#121418]'}`}>{n}</button>)}
      <button data-testid={`${testId}-page-next`} aria-label="Página siguiente" disabled={page === totalPages} onClick={() => onPage(page + 1)} className={style}><ChevronRight size={16} /></button>
    </div>
  </nav>;
};
